// frontend/src/utils/purchaseInvoiceCalc.js

const toNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const round2 = (n) => Math.round((toNumber(n) + Number.EPSILON) * 100) / 100;

/**
 * Calcule les totaux d'une facture avec TVA et remise par ligne
 * Compatible avec le nouveau modèle PurchaseInvoice
 */
export function computePurchaseInvoiceTotals(input) {
  const lines = Array.isArray(input?.lines) ? input.lines : [];

  // Initialiser les récapitulatifs TVA par taux
  const tvaByRate = {
    "0": { subtotal: 0, tva: 0, total: 0 },
    "7": { subtotal: 0, tva: 0, total: 0 },
    "13": { subtotal: 0, tva: 0, total: 0 },
    "19": { subtotal: 0, tva: 0, total: 0 },
  };

  let subtotalHT = 0;
  let totalLineDiscounts = 0;
  let totalTVA = 0;
  let netHTTotal = 0;

  // Calculer chaque ligne
  const computedLines = lines.map((l) => {
    const qty = toNumber(l.qty);
    const unitPriceHT = toNumber(l.unitPriceHT);
    
    // 1. Total HT avant remise
    const lineTotalHT = round2(qty * unitPriceHT);
    
    // 2. Remise de ligne
    const discountType = l.discountType === 'AMOUNT' ? 'AMOUNT' : 'PERCENT';
    const discountValue = toNumber(l.discountValue);
    
    let lineDiscount = 0;
    if (discountType === 'PERCENT') {
      lineDiscount = round2((lineTotalHT * discountValue) / 100);
    } else {
      lineDiscount = round2(discountValue);
    }
    
    // Bornage remise ligne
    if (lineDiscount < 0) lineDiscount = 0;
    if (lineDiscount > lineTotalHT) lineDiscount = lineTotalHT;
    
    // 3. Net HT de la ligne (après remise)
    const lineNetHT = round2(lineTotalHT - lineDiscount);
    
    // 4. TVA de ligne
    const tvaRate = toNumber(l.tvaRate);
    const lineTVA = round2((lineNetHT * tvaRate) / 100);
    
    // 5. Total TTC de la ligne
    const lineTotalTTC = round2(lineNetHT + lineTVA);
    
    // Accumuler dans les totaux
    subtotalHT += lineTotalHT;
    totalLineDiscounts += lineDiscount;
    totalTVA += lineTVA;
    netHTTotal += lineNetHT;
    
    // Accumuler dans le récap TVA par taux
    const rateKey = String(tvaRate);
    if (tvaByRate[rateKey]) {
      tvaByRate[rateKey].subtotal += lineNetHT;
      tvaByRate[rateKey].tva += lineTVA;
      tvaByRate[rateKey].total += lineTotalTTC;
    }
    
    return {
      ...l,
      qty,
      unitPriceHT,
      lineTotalHT,
      discountType,
      discountValue,
      lineDiscount,
      lineNetHT,
      tvaRate,
      lineTVA,
      lineTotalTTC,
    };
  });

  // Arrondir les sous-totaux accumulés
  subtotalHT = round2(subtotalHT);
  totalLineDiscounts = round2(totalLineDiscounts);
  totalTVA = round2(totalTVA);
  netHTTotal = round2(netHTTotal);

  // Gérer la remise globale (optionnelle, pour compatibilité)
  const globalDiscountType = input?.discountType === 'AMOUNT' ? 'AMOUNT' : 'PERCENT';
  const globalDiscountValue = toNumber(input?.discountValue);
  
  let globalDiscount = 0;
  if (globalDiscountValue > 0) {
    if (globalDiscountType === 'PERCENT') {
      globalDiscount = round2((netHTTotal * globalDiscountValue) / 100);
    } else {
      globalDiscount = round2(globalDiscountValue);
    }
    
    // Bornage remise globale
    if (globalDiscount < 0) globalDiscount = 0;
    if (globalDiscount > netHTTotal) globalDiscount = netHTTotal;
    
    // Appliquer la remise globale
    const netHTBeforeGlobalDiscount = netHTTotal;
    netHTTotal = round2(netHTTotal - globalDiscount);
    
    // Recalculer la TVA proportionnellement
    if (netHTBeforeGlobalDiscount > 0) {
      const discountRatio = netHTTotal / netHTBeforeGlobalDiscount;
      totalTVA = round2(totalTVA * discountRatio);
      
      // Ajuster les récaps TVA
      Object.keys(tvaByRate).forEach(rate => {
        tvaByRate[rate].subtotal = round2(tvaByRate[rate].subtotal * discountRatio);
        tvaByRate[rate].tva = round2(tvaByRate[rate].tva * discountRatio);
        tvaByRate[rate].total = round2(tvaByRate[rate].total * discountRatio);
      });
    }
  }

  // Timbre fiscal
  const stampDuty = round2(toNumber(input?.stampDuty));

  // Total TTC
  const totalTTC = round2(netHTTotal + totalTVA);
  
  // Grand total (TTC + timbre)
  const grandTotal = round2(totalTTC + stampDuty);

  return {
    lines: computedLines,
    subtotalHT,
    totalLineDiscounts,
    globalDiscountType,
    globalDiscountValue,
    globalDiscount,
    netHT: netHTTotal,
    totalTVA,
    totalTTC,
    stampDuty,
    grandTotal,
    tvaByRate,
  };
}